// Imports -------------------------------------------------------------------->

import { Service, service } from './di'
import { Request } from '../utils/Router'

// Private Methods ------------------------------------------------------------>

function _readMeta(name: string): string {
    const element = document.querySelector("meta[name='" + name + "']")
    return element ? element.getAttribute('content') : ''
}

// Public Methods ------------------------------------------------------------->

export enum RuntimeConfig {
    DEFAULT,
    DISTRIBUTION,
    PG, // P&G Everyday
}

export enum Experience {
    DEFAULT = 'default',
    DISTRIBUTION = 'distribution',
    PG = 'pg', // P&G Everyday
    SDE = 'sde', // Shopping Destination Experience
}

export enum DistributionMode {
    MODULE,
    SIMPLE,
    INLINE,
    NONE,
}

export const COLOR_BRAND3 = '#ffffff'

@service('configService')
export class ConfigService extends Service {
    ENV = _readMeta('env')
    BUILD = document.documentElement.getAttribute('build')
    DEFAULT_GA_ACCOUNT = _readMeta('default-google-analytics')
    DIST_GA_ACCOUNT = _readMeta('dist-google-analytics')
    GATEFLIPP_SERVER = _readMeta('gateflipp')
    API_SERVER = _readMeta('api_server')
    BACKFLIPP_SERVER_PROXY_CACHE = _readMeta('backflipp-proxy')
    WISHABI_ANALYTICS_SERVER = _readMeta('flipp-analytics')
    WISHABI_TAGMAN_SERVER = _readMeta('tagman')
    DEFAULT_GTAG_ACCOUNT = _readMeta('default-gtag-account')
    GTAG_CONVERSION_ID = _readMeta('gtag_conversion_id')
    GTAG_FLYER_OPEN_CONVERSION_LABEL = _readMeta(
        'gtag_flyer_open_conversion_label'
    )
    GTAG_PUEV_CONVERSION_LABEL = _readMeta('gtag_puev_conversion_label')
    GTAG_EV_CONVERSION_LABEL = _readMeta('gtag_ev_conversion_label')
    APPS_FLYER_WEB_DEV_KEY = _readMeta('appsflyer_web_dev_key')
    ADADAPTED_API_KEY = _readMeta('adadapted_api_key')
    ADADAPTED_ENV = _readMeta('adadapted_env')
    DIST_GTAG_ACCOUNT = _readMeta('dist-gtag-account')
    ACCOUNTS_SERVER = _readMeta('accounts-server')
    ECOM_ITEMS_SERVER = _readMeta('ecom-items-server')
    POSTCARD_SERVER = _readMeta('postcard-server')
    GOOGLE_CLIENT_ID = _readMeta('google-client-id')
    FACEBOOK_APP_ID = _readMeta('facebook-app-id')
    SENTRY_CLIENT_DNS = _readMeta('sentry-client-dns')
    SENTRY_ACCOUNT = _readMeta('sentry-account')
    LAUNCH_DARKLY_ID = _readMeta('launch_darkly_id')
    FLIPP_COM_APIS = _readMeta('flipp_com_apis')
    FLIPP_ADS_APIS = _readMeta('flipp_ads_apis')
    SSR_RENDERER = _readMeta('flipp_ssr_renderer')
    NEXTJS_API = _readMeta('flipp_web_nextjs_api')
    BF_HOSTNAME = _readMeta('bf_hostname')
    BF_CATEGORY = _readMeta('bf_cat')
    DISABLE_MOBILE_BANNERS = _readMeta('disable_mobile_banners')
    HOSTNAME = _readMeta('hostname')
    FLIPP_CANADA_CHANNEL_ID = 119
    FLIPP_US_CHANNEL_ID = 175
    AVRO_BEACONS = _readMeta('avro-beacons')
    NEW_DIST_MODULE_ANALYTICS = _readMeta('new_dist_module_analytics')

    /* RUNTIME defines the runtime behaviour. We need to do a few special things
     * if we're executing for distribution (eg extra param on data fetching,
     * different analytics behaviours), so this config allows us to switch the
     * behaviour as needed.
     */
    RUNTIME =
        _readMeta('experience') === 'pg'
            ? RuntimeConfig.PG
            : RuntimeConfig.DEFAULT

    /* EXPERIENCE defines the UI experience rendered to the user. The distribution
     * site has a different initial experience, and we need a way to toggle that
     * experience on/off depending on how the user is interacting with the site. For
     * distribution, this is based on the URL - if the user first hits the /dist route
     * then we change the experience to "DISTRIBUTION" (see routes.tsx). For
     * P&G Everyday site, we define the experience upfront (ie it's not based on URL
     * path) and it cannot change
     */
    EXPERIENCE =
        _readMeta('experience') === 'pg' ? Experience.PG : Experience.DEFAULT

    /* DISTRIBUTION_MODE defines the source of a distribution experience. A user may arrive here through a module, through
		a link in a publishers website that links them to FW (simple CDP), or through inline embeded iframe on a publishers
		website (inline CDP). Each of these experiences have differences. By default it is NONE
	*/
    DISTRIBUTION_MODE = DistributionMode.NONE

    /**
     * Circular Destination Page
     */
    // CDP_TEST = (function() {
    // 	// tslint:disable-next-line
    // 	debugger;
    // 	return true;
    // })();
    CDP_SIMPLE_HEADER: boolean = false
    CDP_PUBLISHER_NAME: string = ''
    CDP_BACK_LINK: string = ''
    CDP_VISIBLE_SENT: boolean = false
    FORCE_NATIVE: boolean = false
    CDP_COOKIE_EXPIRY = 1 / 720 // in days. equals 2 minutes
    CDP_COOKIE_REFRESH = 1000 * 60 * 1 // in milliseconds. equals 1 minute

    /* PUSHSTATE_SUPPORTED defined whether or not browser History API is properly supported
     * in the current environment. There are specific cases in distrbution where using the
     * history API (eg: history.back()) has bad side effects. In these cases, we fallback to
     * an alternative mechanism (see HistoryService.ts). By default, set to true and we will
     * disable this as needed in a routes.tsx middleware */
    PUSHSTATE_SUPPORTED = true

    private _themeColor: string = COLOR_BRAND3
    private _showAppTopBar: boolean = true
    private _showPageTopBar: boolean = true
    private _suppressedAppTopBar: boolean = false
    private _xbrowseSameMerchMode: boolean = false
    private _xbrowseVisible: boolean = true
    private _showAllFlyersButton: boolean = true
    private _PRODUCTION_HOSTNAME: string = 'flipp.com'
    private _aidHero: boolean = false
    private _moduleId: string = ''
    private _campaignId: string = ''
    private _channelId: string = ''
    private _publisherId: string = ''
    private _deepShareUrl: string = ''

    getThemeColor() {
        return this._themeColor
    }

    updateThemeColor(req: Request) {
        const themeColorParam = 'theme_color'

        if (
            req.params[themeColorParam] &&
            this._isValidColorHex('#' + req.params[themeColorParam])
        ) {
            this._themeColor = '#' + req.params[themeColorParam]
        }
    }

    get xbrowseSameMerchMode(): boolean {
        return this._xbrowseSameMerchMode
    }

    get xbrowseVisible(): boolean {
        return this._xbrowseVisible
    }

    get showPageTopBar(): boolean {
        return this._showPageTopBar
    }

    get showAppTopBar(): boolean {
        return this._showAppTopBar
    }

    get suppressedAppTopBar(): boolean {
        return this._suppressedAppTopBar
    }

    get showAllFlyersButton(): boolean {
        return this._showAllFlyersButton
    }

    get aidHero(): boolean {
        return this._aidHero
    }

    get moduleId(): string {
        return this._moduleId
    }

    get campaignId(): string {
        return this._campaignId
    }

    get publisherId(): string {
        return this._publisherId
    }

    get channelId(): string {
        return this._channelId
    }

    get deepShareUrl(): string {
        return this._deepShareUrl
    }

    updateXBrowse(req: Request) {
        const showAppTopBarParam = 'show_app_top_bar'
        const suppressedAppTopBar = 'suppressed_app_top_bar'
        const showPageTopBarParam = 'show_page_top_bar'
        const xbrowseSameMerchParam = 'xbrowse_same_merch'
        const xbrowseVisibleParam = 'xbrowse'
        const showAllFlyersButtonParam = 'show_all_flyers_button'
        const aid = 'aid'
        const moduleId = 'module_id'
        const campaignId = 'campaign_id'
        const channelId = 'channel_id'
        const publisherId = 'publisher_id'
        const deepShareUrl = 'deep_share_url'

        if (req.params[moduleId]) {
            this._moduleId = req.params[moduleId]
        }
        if (req.params[campaignId]) {
            this._campaignId = req.params[campaignId]
        }
        if (req.params[publisherId]) {
            this._publisherId = req.params[publisherId]
        }
        if (req.params[aid] && req.params[aid] === 'hero_flyers') {
            this._aidHero = true
        }
        if (req.params[channelId]) {
            this._channelId = req.params[channelId]
        }
        if (
            (req.params[showAppTopBarParam] &&
                req.params[showAppTopBarParam] === 'false') ||
            this.EXPERIENCE === Experience.SDE
        ) {
            this._showAppTopBar = false
        }
        if (
            req.params[suppressedAppTopBar] &&
            req.params[suppressedAppTopBar] === 'true'
        ) {
            this._suppressedAppTopBar = true
        }
        if (
            req.params[showPageTopBarParam] &&
            req.params[showPageTopBarParam] === 'false'
        ) {
            this._showPageTopBar = false
        }
        if (
            req.params[xbrowseVisibleParam] &&
            req.params[xbrowseVisibleParam] === 'false'
        ) {
            this._xbrowseVisible = false
        }
        if (
            req.params[xbrowseSameMerchParam] &&
            req.params[xbrowseSameMerchParam] === 'true'
        ) {
            this._xbrowseSameMerchMode = true
        }
        if (
            req.params[xbrowseVisibleParam] &&
            req.params[xbrowseVisibleParam] === 'false'
        ) {
            this._xbrowseVisible = false
        }
        if (
            req.params[showAllFlyersButtonParam] &&
            req.params[showAllFlyersButtonParam] === 'false'
        ) {
            this._showAllFlyersButton = false
        }
        if (req.params[deepShareUrl]) {
            this._deepShareUrl = decodeURIComponent(req.params[deepShareUrl])
        }
    }

    isProduction() {
        return this.HOSTNAME.includes(this._PRODUCTION_HOSTNAME)
    }

    isCDN() {
        return window.location.hostname.includes('cloudfront')
    }

    getCookiesDomain() {
        if (this.HOSTNAME === 'localhost') {
            return 'localhost'
        }

        return 'flipp.com'
    }

    shouldDisableMobileBanners() {
        return (
            this.DISABLE_MOBILE_BANNERS === 'true' ||
            this.EXPERIENCE === Experience.SDE
        )
    }

    isNewDistModuleAnalyticsEnabled() {
        return this.NEW_DIST_MODULE_ANALYTICS === 'true'
    }

    private getRootUrl(path: string) {
        return path.split('/')[1] || null
    }

    private _isValidColorHex(value: any) {
        return /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i.test(value)
    }
}
